import { format } from 'date-fns';

import { strings } from './strings';
import { Kitchen } from '../../libs';
import { partsInKitchen } from '../../libs/utils/kitchenDay';

const GRAMS_PER_KG = 1000;

const kilograms = (grams: number): string => {
  const text = (grams / GRAMS_PER_KG).toFixed(3);
  return text.replace(/0+$/, '').replace(/\.$/, '');
};

/** Display notation, the label's quantity rule: g under 1,000, then kg; ml as is; pieces as "n pcs". */
export const formatQuantity = (quantity: number, unit: string): string => {
  if (unit === 'g' && quantity >= GRAMS_PER_KG) {
    const kg = kilograms(quantity);
    return `${kg.includes('.') ? kg : `${kg}.0`} kg`;
  }
  return `${quantity} ${unit === 'piece' ? 'pcs' : unit}`;
};

/** `dd/MM/yyyy HH:mm` in the kitchen's zone. */
export const formatDateTime = (iso: string, kitchen: Kitchen): string => {
  const p = partsInKitchen(iso, kitchen);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
};

/** `dd/MM HH:mm` in the kitchen's zone. */
export const formatDayTime = (iso: string, kitchen: Kitchen): string => {
  const p = partsInKitchen(iso, kitchen);
  return `${p.day}/${p.month} ${p.hour}:${p.minute}`;
};

/** `HH:mm` in the kitchen's zone. */
export const formatTime = (iso: string, kitchen: Kitchen): string => {
  const p = partsInKitchen(iso, kitchen);
  return `${p.hour}:${p.minute}`;
};

/** The name of a trend bucket in the kitchen's zone: `HH:00` for an hour, `dd/MM` for a day. */
export const formatBucket = (iso: string, size: 'HOUR' | 'DAY', kitchen: Kitchen): string => {
  const p = partsInKitchen(iso, kitchen);
  return size === 'HOUR' ? `${p.hour}:00` : `${p.day}/${p.month}`;
};

/** `dd/MM/yyyy` from a `yyyy-MM-dd` string, by reordering its parts. */
export const formatDay = (day: string): string => day.split('-').reverse().join('/');

export const LABEL_ID_GROUP_LENGTH = 5;

/** A label ID in the groups it prints in. `LabelId` spaces them with CSS only, so text a screen reader speaks needs the space. */
export const groupedLabelId = (id: string): string => `${id.slice(0, LABEL_ID_GROUP_LENGTH)} ${id.slice(LABEL_ID_GROUP_LENGTH)}`;

const twoDigits = (n: number): string => String(n).padStart(2, '0');

/** The `yyyy-MM-dd` of a picker date from its local parts, which is what the user picked. */
export const localDay = (date: Date): string =>
  `${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}`;

/** The local-midnight `Date` a picker shows for a `yyyy-MM-dd` day. */
export const dayToLocalDate = (day: string): Date => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
};

/** `19 Oct – 25 Oct`: a range without its year, for the button that carries it. */
export const formatRange = (from: string, to: string): string =>
  strings.accounts.rangeLabel(format(dayToLocalDate(from), 'd MMM'), format(dayToLocalDate(to), 'd MMM'));

/** `Tue 3 Nov 2026` for one day, `26 Oct – 1 Nov 2026` for a range. */
export const formatWindow = (from: string, to: string): string =>
  from === to ? format(dayToLocalDate(from), 'EEE d MMM yyyy') : `${formatRange(from, to)} ${format(dayToLocalDate(to), 'yyyy')}`;
